import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { ZInput, ZTextarea, ZSelect } from "@/components/ZInput";
import ZButton from "@/components/ZButton";
import { Plus, Trash2 } from "lucide-react";

const TYPES = [
  { id: "fundraiser", label: "Cagnotte" },
  { id: "vote", label: "Vote" },
  { id: "survey", label: "Sondage" },
];

const QTYPES = [
  { id: "single", label: "Choix unique" },
  { id: "multi", label: "Choix multiple" },
  { id: "star", label: "Notation (étoiles)" },
  { id: "text", label: "Réponse libre" },
];

export default function EngagementCreatePage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const eventIdFromUrl = params.get("event_id") || "";
  const [type, setType] = useState("fundraiser");
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState(eventIdFromUrl);

  // Fundraiser
  const [fr, setFr] = useState({ title: "", description: "", goal: 500, image_url: "" });

  // Vote
  const [vote, setVote] = useState({
    title: "", description: "", vote_type: "single",
    options: ["", ""],
    restricted: false, quorum_pct: 0,
    starts_at: "", ends_at: "",
  });

  // Survey
  const [survey, setSurvey] = useState({
    title: "", description: "",
    questions: [{ text: "", type: "single", options: ["", ""] }],
  });

  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/organizer/events").then((r) => setEvents(r.data));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      if (type === "fundraiser") {
        if (!fr.title.trim()) throw new Error("Titre obligatoire");
        const payload = { title: fr.title, description: fr.description, goal: parseFloat(fr.goal) || 0, image_url: fr.image_url, event_id: eventId || null };
        await api.post("/fundraisers", payload);
        nav("/org/events");
      } else if (type === "vote") {
        if (!vote.title.trim()) throw new Error("Titre obligatoire");
        const payload = {
          title: vote.title,
          description: vote.description,
          vote_type: vote.vote_type,
          anonymous: true,
          event_id: eventId || null,
          restricted: vote.restricted,
          quorum_pct: parseFloat(vote.quorum_pct) || 0,
        };
        if (vote.starts_at) payload.starts_at = new Date(vote.starts_at).toISOString();
        if (vote.ends_at) payload.ends_at = new Date(vote.ends_at).toISOString();
        if (vote.vote_type === "binary") {
          payload.options = [];
        } else {
          const opts = vote.options.filter((o) => o.trim()).map((o) => ({ label: o.trim() }));
          if (opts.length < 2) throw new Error("Au moins 2 options.");
          payload.options = opts;
          payload.allow_multi = vote.vote_type === "multi";
        }
        const res = await api.post("/votes", payload);
        if (vote.restricted) {
          nav(`/org/vote/${res.data.id}/electoral-list`);
        } else {
          nav("/org/events");
        }
      } else {
        if (!survey.title.trim()) throw new Error("Titre obligatoire");
        const questions = survey.questions.map((q) => ({
          text: q.text.trim(), type: q.type,
          options: q.type === "single" || q.type === "multi" ? q.options.filter((o) => o.trim()) : [],
        })).filter((q) => q.text);
        if (questions.length === 0) throw new Error("Au moins une question.");
        for (const q of questions) {
          if ((q.type === "single" || q.type === "multi") && q.options.length < 2) {
            throw new Error("2 options minimum pour " + q.text);
          }
        }
        await api.post("/surveys", { title: survey.title, description: survey.description, questions, event_id: eventId || null });
        nav("/org/events");
      }
    } catch (e) {
      setErr(e.message || errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="z-container" style={{ maxWidth: 640 }} data-testid="engagement-create-page">
      <h1 className="z-page-title">Lancer un engagement</h1>
      <p className="z-page-sub">Cagnotte, vote ou sondage rattaché (ou non) à un événement.</p>

      <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
        {TYPES.map((t) => (
          <button key={t.id} type="button" onClick={() => setType(t.id)} className={`z-chip ${type === t.id ? "z-chip-active" : ""}`} data-testid={`engagement-type-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="z-card" style={{ padding: 24 }}>
        <ZSelect label="Événement associé (optionnel)" value={eventId} onChange={(e) => setEventId(e.target.value)} data-testid="engagement-event-select">
          <option value="">Aucun</option>
          {events.map((e) => <option key={e.id} value={e.id}>{e.title}</option>)}
        </ZSelect>

        {type === "fundraiser" && (
          <>
            <ZInput label="Titre" value={fr.title} onChange={(e) => setFr({ ...fr, title: e.target.value })} required data-testid="fr-title" />
            <ZTextarea label="Description" value={fr.description} onChange={(e) => setFr({ ...fr, description: e.target.value })} data-testid="fr-description" />
            <ZInput label="Objectif (€)" type="number" min="1" value={fr.goal} onChange={(e) => setFr({ ...fr, goal: e.target.value })} data-testid="fr-goal" />
            <ZInput label="URL image (optionnel)" value={fr.image_url} onChange={(e) => setFr({ ...fr, image_url: e.target.value })} placeholder="https://..." data-testid="fr-image" />
          </>
        )}

        {type === "vote" && (
          <>
            <ZInput label="Question / Titre" value={vote.title} onChange={(e) => setVote({ ...vote, title: e.target.value })} required data-testid="vote-form-title" />
            <ZTextarea label="Description" value={vote.description} onChange={(e) => setVote({ ...vote, description: e.target.value })} data-testid="vote-form-description" />

            <ZSelect label="Type de scrutin" value={vote.vote_type} onChange={(e) => setVote({ ...vote, vote_type: e.target.value })} data-testid="vote-form-type">
              <option value="single">Choix unique (1 option parmi N)</option>
              <option value="multi">Choix multiples (plusieurs parmi N)</option>
              <option value="binary">Binaire (Oui / Non / Abstention)</option>
              <option value="approval">Approbation (✓/✗ par option)</option>
              <option value="ranked">Par rang (classement de préférence)</option>
            </ZSelect>

            {vote.vote_type !== "binary" && (
              <>
                <label className="z-label">Options</label>
                {vote.options.map((o, i) => (
                  <div key={i} style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                    <input className="z-input" value={o} onChange={(e) => {
                      const a = [...vote.options]; a[i] = e.target.value; setVote({ ...vote, options: a });
                    }} placeholder={`Option ${i + 1}`} data-testid={`vote-option-input-${i}`} />
                    {vote.options.length > 2 && (
                      <button type="button" onClick={() => setVote({ ...vote, options: vote.options.filter((_, x) => x !== i) })} className="z-btn z-btn-ghost" style={{ color: "var(--z-error)" }} data-testid={`vote-option-remove-${i}`}>
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={() => setVote({ ...vote, options: [...vote.options, ""] })} className="z-btn z-btn-outline z-btn-sm" data-testid="vote-add-option-btn"><Plus size={14} /> Ajouter une option</button>
              </>
            )}

            <div className="z-divider" />

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <ZInput type="datetime-local" label="Ouverture (optionnel)" value={vote.starts_at} onChange={(e) => setVote({ ...vote, starts_at: e.target.value })} data-testid="vote-starts" />
              <ZInput type="datetime-local" label="Clôture (optionnel)" value={vote.ends_at} onChange={(e) => setVote({ ...vote, ends_at: e.target.value })} data-testid="vote-ends" />
            </div>

            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, marginTop: 10, marginBottom: 10, cursor: "pointer" }}>
              <input type="checkbox" checked={vote.restricted} onChange={(e) => setVote({ ...vote, restricted: e.target.checked })} data-testid="vote-restricted" style={{ accentColor: "var(--z-teal)" }} />
              Restreindre à une liste électorale (vote officiel)
            </label>

            {vote.restricted && (
              <>
                <ZInput type="number" min="0" max="100" step="1" label="Quorum (% participation minimum)" value={vote.quorum_pct} onChange={(e) => setVote({ ...vote, quorum_pct: e.target.value })} help="0 = pas de quorum" data-testid="vote-quorum" />
                <p style={{ fontSize: 13, color: "var(--z-muted)" }}>
                  Vous configurerez la liste des votants (e-mails + pondération) après création.
                </p>
              </>
            )}
          </>
        )}

        {type === "survey" && (
          <>
            <ZInput label="Titre" value={survey.title} onChange={(e) => setSurvey({ ...survey, title: e.target.value })} required data-testid="survey-form-title" />
            <ZTextarea label="Description" value={survey.description} onChange={(e) => setSurvey({ ...survey, description: e.target.value })} data-testid="survey-form-description" />
            {survey.questions.map((q, qi) => (
              <div key={qi} className="z-card" style={{ padding: 14, marginBottom: 14, background: "var(--z-bg)" }}>
                <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                  <input className="z-input" value={q.text} onChange={(e) => {
                    const a = [...survey.questions]; a[qi] = { ...a[qi], text: e.target.value }; setSurvey({ ...survey, questions: a });
                  }} placeholder={`Question ${qi + 1}`} data-testid={`survey-q-text-${qi}`} />
                  {survey.questions.length > 1 && (
                    <button type="button" onClick={() => setSurvey({ ...survey, questions: survey.questions.filter((_, x) => x !== qi) })} className="z-btn z-btn-ghost" style={{ color: "var(--z-error)" }} data-testid={`survey-q-remove-${qi}`}>
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
                <select className="z-select" value={q.type} onChange={(e) => {
                  const a = [...survey.questions]; a[qi] = { ...a[qi], type: e.target.value }; setSurvey({ ...survey, questions: a });
                }} data-testid={`survey-q-type-${qi}`}>
                  {QTYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
                {(q.type === "single" || q.type === "multi") && (
                  <div style={{ marginTop: 8 }}>
                    {q.options.map((o, oi) => (
                      <div key={oi} style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                        <input className="z-input" value={o} onChange={(e) => {
                          const a = [...survey.questions];
                          const opts = [...a[qi].options]; opts[oi] = e.target.value;
                          a[qi] = { ...a[qi], options: opts }; setSurvey({ ...survey, questions: a });
                        }} placeholder={`Option ${oi + 1}`} />
                        {q.options.length > 2 && (
                          <button type="button" onClick={() => {
                            const a = [...survey.questions];
                            a[qi] = { ...a[qi], options: a[qi].options.filter((_, x) => x !== oi) };
                            setSurvey({ ...survey, questions: a });
                          }} className="z-btn z-btn-ghost z-btn-sm" style={{ color: "var(--z-error)" }}><Trash2 size={14} /></button>
                        )}
                      </div>
                    ))}
                    <button type="button" onClick={() => {
                      const a = [...survey.questions];
                      a[qi] = { ...a[qi], options: [...a[qi].options, ""] };
                      setSurvey({ ...survey, questions: a });
                    }} className="z-btn z-btn-outline z-btn-sm"><Plus size={14} /> Option</button>
                  </div>
                )}
              </div>
            ))}
            <button type="button" onClick={() => setSurvey({ ...survey, questions: [...survey.questions, { text: "", type: "single", options: ["", ""] }] })} className="z-btn z-btn-outline z-btn-sm" data-testid="survey-add-question-btn"><Plus size={14} /> Ajouter une question</button>
          </>
        )}

        {err && <p className="z-error-text" data-testid="engagement-error">{err}</p>}
        <ZButton type="submit" block loading={loading} data-testid="engagement-submit-btn" style={{ marginTop: 18 }}>
          Lancer
        </ZButton>
      </form>
    </div>
  );
}
